import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import {
  CustomerConditionType,
  GATriggerType,
  NodeType,
} from '../../app/types';
import icLeft from '../../assets/images/svg/ic-arrow-left.svg';
import icRight from '../../assets/images/svg/ic-arrow-right.svg';
import {
  setDefaultTemplate,
  setIsShowSettingBar,
  setWhatsappTemplate,
} from '../../features/mainSlice';
import ConditionSettingBar from './ConditionSettingBar';
import CouponSettingBar from './CouponSettingBar';
import CustomerConditionSettingBar from './CustomerConditionSettingBar';
import DefaultTemplateContainer from './DefaultTemplateContainer';
import GAConditionSettingBar from './GAConditionSettingBar';
import InboxSettingBar from './InboxSettingBar';
import MASettingBar from './MASettingBar';
import MessageSettingBar from './MessageSettingBar';
import ScheduleSettingBar from './ScheduleSettingBar';
import TagSettingBar from './TagSettingBar';
import TriggerSettingBar from './TriggerSettingBar';
import WaitSettingBar from './WaitSettingBar';
import WhatsappTemplateContainer from './WhatsappTemplateContainer';

const useStyles = makeStyles(
  {
    root: {
      display: 'flex',
      alignItems: 'start',
      position: 'fixed',
      top: 114,
      right: '0',
      zIndex: 999,
    },
    btn: {
      background: color.$3,
      padding: '14px 16px',
      boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
      cursor: 'pointer',
    },
    bar: {
      width: 360,
      boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
      minHeight: 'calc(100vh - 114px)',
      background: color.$7,
    },
    none: {
      display: 'none',
    },
    top: {
      fontSize: '17px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      background: color.$9,
      textAlign: 'center',
      padding: '18px 0 21px',
    },
    bottom: {
      padding: '24px 16px',
    },
  },
  {
    name: 'SettingBar',
  },
);

const SettingBar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { selectedNode, isShowSettingBar } = useSelector(store => store.ma);
  const showDefaultTemplate = useSelector(
    store => store.ma.defaultTemplate.isShow,
  );
  const showWhatsappTemplate = useSelector(
    store => store.ma.whatsappTemplate.isShow,
  );
  const { type, data } = selectedNode || {};
  const { triggerType } = data || {};

  const isGAGroup = useMemo(
    () => Object.values(GATriggerType).includes(triggerType),
    [triggerType],
  );

  const isCustomerGroup = useMemo(
    () => Object.values(CustomerConditionType).includes(triggerType),
    [triggerType],
  );

  const contentComponent = useMemo(() => {
    switch (type) {
      case NodeType.WAIT:
        return <WaitSettingBar />;
      case NodeType.SCHEDULE:
        return <ScheduleSettingBar />;
      case NodeType.TAGGING:
        return <TagSettingBar />;
      case NodeType.INBOX:
        return <InboxSettingBar />;
      case NodeType.MESSAGE:
        return <MessageSettingBar />;
      case NodeType.COUPON:
        return <CouponSettingBar />;
      case NodeType.MA:
        return <MASettingBar />;
      case NodeType.TRIGGER:
        return <TriggerSettingBar />;
      case NodeType.GROUP:
        if (isGAGroup) {
          return <GAConditionSettingBar />;
        }
        if (isCustomerGroup) {
          return <CustomerConditionSettingBar />;
        }
        return <ConditionSettingBar />;
      default:
        return null;
    }
  }, [type, triggerType]);

  useEffect(() => {
    dispatch(setIsShowSettingBar(!!selectedNode));
    if (!selectedNode) {
      dispatch(
        setDefaultTemplate({
          isShow: false,
        }),
      );
      dispatch(
        setWhatsappTemplate({
          isShow: false,
        }),
      );
    }
  }, [selectedNode]);

  useEffect(() => {
    dispatch(setIsShowSettingBar(false));
    dispatch(
      setDefaultTemplate({
        isShow: false,
      }),
    );
    dispatch(
      setWhatsappTemplate({
        isShow: false,
      }),
    );
  }, []);

  return (
    <div className={classes.root}>
      {showDefaultTemplate ? (
        <DefaultTemplateContainer />
      ) : showWhatsappTemplate ? (
        <WhatsappTemplateContainer />
      ) : (
        <div
          className={classes.btn}
          onClick={() => {
            if (!selectedNode) {
              dispatch(setIsShowSettingBar(false));
              dispatch(
                setDefaultTemplate({
                  isShow: false,
                }),
              );
            } else {
              dispatch(setIsShowSettingBar(!isShowSettingBar));
            }
          }}
        >
          <img src={isShowSettingBar ? icRight : icLeft} alt="import" />
        </div>
      )}
      <div
        className={classnames(classes.bar, {
          [classes.none]: !isShowSettingBar,
        })}
      >
        {contentComponent}
      </div>
    </div>
  );
};

export default SettingBar;
